import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ChevronDown, ChevronUp, Copy, KeyRound, RefreshCw, ShieldCheck, Sparkles, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { useToast } from '../hooks/use-toast';

const CHARACTER_SETS = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  numbers: '0123456789',
  special: '!@#$%^&*()_+-=[]{}|;:,.<>?',
};

const secureRandomIndex = (maximum) => {
  if (maximum <= 0) return 0;
  const range = 0x100000000;
  const limit = range - (range % maximum);
  const random = new Uint32Array(1);

  do {
    window.crypto.getRandomValues(random);
  } while (random[0] >= limit);

  return random[0] % maximum;
};

const secureShuffle = (characters) => {
  const shuffled = [...characters];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = secureRandomIndex(index + 1);
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled.join('');
};

const PasswordGeneratorPage = () => {
  const { toast } = useToast();
  const [length, setLength] = useState(16);
  const [options, setOptions] = useState({
    special: true,
    numbers: true,
    uppercase: true,
    lowercase: true,
    noSpaces: true,
  });
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(
    () => localStorage.getItem('password-generator-bookmark-dismissed') !== 'true'
  );

  const enabledSets = [
    options.uppercase && CHARACTER_SETS.uppercase,
    options.lowercase && CHARACTER_SETS.lowercase,
    options.numbers && CHARACTER_SETS.numbers,
    options.special && CHARACTER_SETS.special,
  ].filter(Boolean);

  const generatePassword = useCallback(() => {
    const selectedSets = [
      options.uppercase && CHARACTER_SETS.uppercase,
      options.lowercase && CHARACTER_SETS.lowercase,
      options.numbers && CHARACTER_SETS.numbers,
      options.special && CHARACTER_SETS.special,
    ].filter(Boolean);

    const pool = `${selectedSets.join('')}${options.noSpaces ? '' : ' '}`;
    if (!pool) {
      setPassword('');
      return;
    }

    const required = selectedSets.map((set) => set[secureRandomIndex(set.length)]);
    const generated = [...required];
    while (generated.length < length) {
      generated.push(pool[secureRandomIndex(pool.length)]);
    }
    setPassword(secureShuffle(generated.slice(0, length)));
    setCopied(false);
  }, [length, options]);

  useEffect(() => {
    generatePassword();
  }, [generatePassword]);

  const updateOption = (name, checked) => {
    setOptions((current) => ({ ...current, [name]: Boolean(checked) }));
  };

  const updateLength = (value) => {
    const parsed = Number(value);
    setLength(Math.min(128, Math.max(4, Number.isFinite(parsed) ? parsed : 16)));
  };

  const copyPassword = async () => {
    if (!password) return;
    await navigator.clipboard.writeText(password);
    setCopied(true);
    toast({ title: 'Password copied', description: 'The generated password is on your clipboard.' });
    window.setTimeout(() => setCopied(false), 1800);
  };

  const strength = length >= 20 && enabledSets.length >= 3
    ? 'Very strong'
    : length >= 12 && enabledSets.length >= 2
      ? 'Strong'
      : 'Moderate';

  const optionRows = [
    ['special', 'Add Special Characters', 'Include symbols such as !, @, #, and %.'],
    ['numbers', 'Add Numbers', 'Include digits from 0 to 9.'],
    ['uppercase', 'Add Capital Case', 'Include uppercase letters from A to Z.'],
    ['lowercase', 'Add Small Case', 'Include lowercase letters from a to z.'],
    ['noSpaces', "Don't Add Space", 'Keep spaces out of the generated password.'],
  ];

  const bookmarkPage = () => {
    const shortcut = navigator.userAgent.toLowerCase().includes('mac') ? 'Cmd+D' : 'Ctrl+D';
    alert(`To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to the password generator.`);
  };

  const dismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('password-generator-bookmark-dismissed', 'true');
  };

  const faqs = [
    {
      question: 'Is this password generator secure?',
      answer: 'Yes. Passwords are generated with the Web Crypto API, which uses your browser’s cryptographically secure random number generator. Generation happens entirely on your device.',
    },
    {
      question: 'Does AllSmallTools save my generated passwords?',
      answer: 'No. Generated passwords are not uploaded, logged, or stored by this tool. They exist only in the current browser page unless you copy and save them elsewhere.',
    },
    {
      question: 'What password length should I choose?',
      answer: 'For most accounts, use at least 16 characters with uppercase and lowercase letters, numbers, and symbols. Longer unique passwords are generally harder to guess.',
    },
    {
      question: 'Why would I allow spaces in a password?',
      answer: 'Some services support spaces and passphrases, while others do not. Keep “Don’t Add Space” selected unless you know the destination accepts spaces.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Password Generator — Create Strong Secure Passwords | AllSmallTooLS</title>
        <meta name="description" content="Generate a strong, random password with custom length, letters, numbers, symbols, and spacing options. Free and private." />
        <meta name="keywords" content="password generator, strong password generator, random password, secure password" />
        <link rel="canonical" href="https://allsmalltools.com/password-generator" />
      </Helmet>

      <div className="space-y-6">
        {showBookmarkBanner && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-3 text-center sm:text-left">
                <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0"><KeyRound className="h-6 w-6 text-indigo-600" /></div>
                <div>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">Bookmark this page for secure passwords anytime</p>
                  <p className="text-sm text-gray-600 hidden sm:block">Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={bookmarkPage} className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 whitespace-nowrap">Bookmark Now</button>
                <button onClick={dismissBookmarkBanner} className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-3 rounded-lg shadow-sm" aria-label="Dismiss bookmark banner">✕</button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center space-x-4">
          <Link to="/">
            <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Back to Home</Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2"><KeyRound className="h-8 w-8 text-violet-600" /><span>Password Generator</span></h1>
            <p className="text-gray-600">Create a strong, random password based on your preferences</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader><CardTitle>Password Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor="password-length" className="text-base font-semibold">Password Length</Label>
                  <Input id="password-length" type="number" min="4" max="128" value={length} onChange={(event) => updateLength(event.target.value)} className="w-24 text-center font-semibold" />
                </div>
                <Slider value={[length]} min={4} max={128} step={1} onValueChange={([value]) => setLength(value)} aria-label="Password length" />
                <div className="flex justify-between text-xs text-gray-500"><span>4 characters</span><span>128 characters</span></div>
              </div>

              <div className="space-y-3">
                <h2 className="text-base font-semibold text-gray-900">Character Options</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {optionRows.map(([name, label, description]) => (
                    <label key={name} htmlFor={`option-${name}`} className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:border-violet-300 hover:bg-violet-50 transition-colors">
                      <Checkbox id={`option-${name}`} checked={options[name]} onCheckedChange={(checked) => updateOption(name, checked)} className="mt-0.5" />
                      <span><span className="block font-medium text-gray-900">{label}</span><span className="block text-xs text-gray-500 mt-1">{description}</span></span>
                    </label>
                  ))}
                </div>
                {!password && <p className="text-sm text-red-600">Select at least one character option, or allow spaces.</p>}
              </div>

              <Button onClick={generatePassword} disabled={!password} className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600">
                <RefreshCw className="h-4 w-4 mr-2" />Generate New Password
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Password Result</CardTitle></CardHeader>
            <CardContent>
              {password ? (
                <div className="space-y-6">
                  <div className="rounded-lg bg-violet-50 border border-violet-200 p-5">
                    <Label htmlFor="generated-password" className="text-sm font-semibold text-violet-900">Your secure password</Label>
                    <div className="mt-3 flex flex-col sm:flex-row gap-3">
                      <Input id="generated-password" value={password} readOnly aria-label="Generated password" className="h-14 font-mono text-lg bg-white" />
                      <Button onClick={copyPassword} className="h-14 px-6 bg-violet-600 hover:bg-violet-700">
                        {copied ? <Check className="h-5 w-5 mr-2" /> : <Copy className="h-5 w-5 mr-2" />}{copied ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
                    <ShieldCheck className="h-6 w-6 flex-shrink-0" />
                    <div><p className="font-semibold">{strength} password</p><p className="text-sm">Includes {length} characters and {enabledSets.length} selected character groups.</p></div>
                  </div>
                  <p className="text-sm text-gray-600">Copy this password and save it in a trusted password manager. It is not stored by AllSmallTools.</p>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500"><KeyRound className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No password can be generated</p><p className="text-sm">Enable at least one character option</p></div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="mt-12">
          <CardHeader><h2 className="text-2xl font-bold text-gray-900">Why Use Our Password Generator?</h2></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                [Zap, 'Fast & Easy', 'Create a strong password instantly. Adjust the settings and generate as many unique passwords as you need.'],
                [ShieldCheck, 'Secure & Private', 'Generation happens entirely in your browser using cryptographically secure randomness. Nothing is uploaded or saved.'],
                [Sparkles, 'Fully Customizable', 'Control the length, letter cases, numbers, special characters, and whether spaces are allowed.'],
              ].map(([Icon, title, description]) => (
                <div key={title} className="flex flex-col items-start space-y-3">
                  <div className="bg-violet-100 p-3 rounded-lg"><Icon className="h-6 w-6 text-violet-600" /></div>
                  <h3 className="text-lg font-semibold text-gray-900">{title}</h3><p className="text-gray-600">{description}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-gray-50 rounded-lg"><p className="text-gray-700">Use a different password for every account and store important passwords in a reputable password manager. Longer passwords with multiple character types provide stronger protection against guessing attacks.</p></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><h2 className="text-xl font-bold text-gray-900">How to Generate a Secure Password</h2></CardHeader>
          <CardContent>
            <ol className="space-y-4 text-gray-700">
              {[
                ['Choose Password Length', 'Use the slider or number field to select between 4 and 128 characters.'],
                ['Select Character Options', 'Choose uppercase letters, lowercase letters, numbers, special characters, and whether spaces should be excluded.'],
                ['Generate Your Password', 'Your password updates automatically, or click “Generate New Password” for another secure combination.'],
                ['Copy and Save Securely', 'Click Copy, then save the password in a trusted password manager.'],
              ].map(([title, description], index) => (
                <li key={title} className="flex items-start space-x-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center text-sm font-semibold">{index + 1}</span><div><strong>{title}:</strong> {description}</div></li>
              ))}
            </ol>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {faqs.map((faq, index) => (
                <div key={faq.question} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors" aria-expanded={openFaq === index}>
                    <h3 className="font-semibold text-gray-900">{faq.question}</h3>{openFaq === index ? <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />}
                  </button>
                  {openFaq === index && <div className="p-4 bg-white"><p className="text-gray-700">{faq.answer}</p></div>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default PasswordGeneratorPage;
